import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Mail, MessageSquare, ShieldAlert, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BASE = "/business-coach/notifications/verify";

export default function CoachVerificationCenter() {
  const nav = useNavigate();
  const [resend, setResend] = useState(null);
  const [twilio, setTwilio] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, t, a] = await Promise.all([
        api.get(`${BASE}/resend/preflight`),
        api.get(`${BASE}/twilio/preflight`),
        api.get(`${BASE}/audit-log`),
      ]);
      setResend(r.data); setTwilio(t.data); setAudit(a.data || []);
    } catch (e) {
      toast.error("Load failed: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="coach-verification-center">
      <button onClick={() => nav("/app/coach")} data-testid="verify-back"
              className="flex items-center gap-1 text-sm text-zinc-500 hover:text-[#FF5A00] mb-3">
        <ArrowLeft className="w-4 h-4"/> Back to Coach
      </button>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Verification Center</div>
          <h1 className="font-heading text-3xl font-bold">Live Resend + Twilio activation</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Nothing goes live until every step passes. Credentials are read from
            secure environment variables only — never paste secrets into this UI, chat, or code.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="verify-refresh">
          <RefreshCw className="w-3 h-3 mr-1"/> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <ChannelPanel
          icon={<Mail className="w-5 h-5"/>}
          label="Resend (Email)"
          channel="resend"
          state={resend}
          onDone={load}
          testid="verify-resend"
        />
        <ChannelPanel
          icon={<MessageSquare className="w-5 h-5"/>}
          label="Twilio (SMS)"
          channel="twilio"
          state={twilio}
          onDone={load}
          testid="verify-twilio"
        />
      </div>

      <h2 className="font-heading text-xl font-bold mb-2">Audit log</h2>
      <Card className="p-3 overflow-x-auto">
        {audit.length === 0 && <div className="text-sm text-zinc-500 p-2" data-testid="verify-audit-empty">No verification events yet.</div>}
        {audit.length > 0 && (
          <table className="w-full text-xs" data-testid="verify-audit-table">
            <thead className="text-zinc-500 uppercase">
              <tr>
                <th className="text-left px-2 py-1">When</th>
                <th className="text-left px-2 py-1">Channel</th>
                <th className="text-left px-2 py-1">Event</th>
                <th className="text-left px-2 py-1">User</th>
                <th className="text-left px-2 py-1">Payload</th>
              </tr>
            </thead>
            <tbody>
              {audit.slice(0, 40).map((r) => (
                <tr key={r.id} className="border-t border-zinc-100">
                  <td className="px-2 py-1 whitespace-nowrap">{(r.at || "").replace("T", " ").slice(0, 16)}</td>
                  <td className="px-2 py-1 font-mono">{r.channel}</td>
                  <td className="px-2 py-1">{r.event}</td>
                  <td className="px-2 py-1 text-zinc-500">{(r.user_id || "").slice(-6)}</td>
                  <td className="px-2 py-1 text-zinc-500 max-w-xs truncate">{JSON.stringify(r.payload)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function ChannelPanel({ icon, label, channel, state, onDone, testid }) {
  const env = state?.env || {};
  const st = state?.state || {};
  const isLive = !!st.live_enabled;
  const gateReady = st.preflight_passed && st.test_sent && st.arrival_confirmed;
  const [target, setTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [twConsent, setTwConsent] = useState(false);
  const [twStopStart, setTwStopStart] = useState(false);
  const [twQuiet, setTwQuiet] = useState(false);
  const [twDupe, setTwDupe] = useState(false);
  const [twCb, setTwCb] = useState(false);

  const post = async (path, body = {}) => {
    try {
      const { data } = await api.post(`${BASE}/${channel}/${path}`, body);
      toast.success(`${channel} · ${path} ok`);
      onDone?.();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${path} failed`);
      onDone?.();
      throw e;
    }
  };

  return (
    <Card className={`p-4 ${isLive ? "border-emerald-300" : "border-zinc-200"}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <div className="font-semibold">{label}</div>
            <div className="text-[11px] text-zinc-500">
              {isLive ? "LIVE — recurring sends can flow" : "gated — preview only"}
            </div>
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
          isLive ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"
        }`} data-testid={`${testid}-status`}>
          {isLive ? "enabled" : "disabled"}
        </span>
      </div>

      <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded p-2 mb-3">
        <div className="uppercase tracking-wider text-zinc-400 text-[9px] mb-1">Environment</div>
        <ul className="space-y-0.5">
          {Object.entries(env).map(([k, v]) => (
            <li key={k}>
              <span className="font-mono">{k}</span>:{" "}
              <span className={typeof v === "boolean" ? (v ? "text-emerald-600" : "text-rose-600") : ""}>
                {typeof v === "boolean" ? (v ? "✓" : "✗") : Array.isArray(v) ? (v.length ? v.join(", ") : "(none)") : (v || "—")}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <ol className="space-y-2 text-sm">
        <li className="flex items-start gap-2">
          {st.preflight_passed ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5"/> : <XCircle className="w-4 h-4 text-zinc-400 mt-0.5"/>}
          <div className="flex-1">
            <div className="font-medium">Preflight</div>
            {st.preflight_issues?.length > 0 && (
              <ul className="text-[11px] text-rose-600 list-disc pl-4">
                {st.preflight_issues.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            )}
            <Button size="sm" variant="outline" className="mt-1"
                    onClick={() => post("preflight", {})}
                    data-testid={`${testid}-preflight-btn`}>
              Run preflight
            </Button>
          </div>
        </li>
        <li className="flex items-start gap-2">
          {st.test_sent ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5"/> : <XCircle className="w-4 h-4 text-zinc-400 mt-0.5"/>}
          <div className="flex-1">
            <div className="font-medium">Test send {st.test_target ? `→ ${st.test_target}` : ""}</div>
            <div className="flex gap-2 mt-1">
              <Input placeholder={channel === "resend" ? "you@example.com" : "+15550000000"}
                     value={target} onChange={(e) => setTarget(e.target.value)}
                     className="text-xs h-8" data-testid={`${testid}-target`}/>
              <Button size="sm" variant="outline"
                      disabled={!st.preflight_passed || !target}
                      onClick={() => post("test-send", { to: target })}
                      data-testid={`${testid}-test-send-btn`}>
                Send test
              </Button>
            </div>
            {st.last_error && <div className="text-[11px] text-rose-600 mt-1">{st.last_error}</div>}
          </div>
        </li>
        <li className="flex items-start gap-2">
          {st.arrival_confirmed ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5"/> : <XCircle className="w-4 h-4 text-zinc-400 mt-0.5"/>}
          <div className="flex-1">
            <div className="font-medium">Confirm arrival + formatting</div>
            <Textarea placeholder="Delivery notes (visible in audit log)" value={notes}
                        onChange={(e) => setNotes(e.target.value)} rows={2}
                        className="text-xs mt-1" data-testid={`${testid}-notes`}/>
            {channel === "twilio" && (
              <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
                <label className="flex items-center gap-1"><input type="checkbox" checked={twConsent} onChange={(e) => setTwConsent(e.target.checked)} data-testid={`${testid}-consent`}/> Recipient consent captured</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={twStopStart} onChange={(e) => setTwStopStart(e.target.checked)} data-testid={`${testid}-stopstart`}/> STOP/START tested</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={twQuiet} onChange={(e) => setTwQuiet(e.target.checked)} data-testid={`${testid}-quiet`}/> Quiet hours confirmed</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={twDupe} onChange={(e) => setTwDupe(e.target.checked)} data-testid={`${testid}-dupe`}/> Duplicate prevention</label>
                <label className="flex items-center gap-1 col-span-2"><input type="checkbox" checked={twCb} onChange={(e) => setTwCb(e.target.checked)} data-testid={`${testid}-cb`}/> Delivery callback received</label>
              </div>
            )}
            <Button size="sm" variant="outline" className="mt-2"
                    disabled={!st.test_sent}
                    onClick={() => {
                      const body = channel === "twilio" ? {
                        notes, consent_ok: twConsent, stop_start_ok: twStopStart,
                        quiet_hours_ok: twQuiet, duplicate_prevention_ok: twDupe,
                        delivery_callback_ok: twCb,
                      } : { notes, unsubscribe_visible: true, deep_links_ok: true, formatting_ok: true };
                      post("confirm-arrival", body);
                    }}
                    data-testid={`${testid}-confirm-btn`}>
              Confirm arrival
            </Button>
          </div>
        </li>
        <li className="flex items-start gap-2">
          {isLive ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5"/> : <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5"/>}
          <div className="flex-1">
            <div className="font-medium">Enable live sends</div>
            <div className="flex gap-2 mt-1">
              <Button size="sm" disabled={!gateReady || isLive}
                      className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                      onClick={async () => {
                        const reason = window.prompt("Approval reason (recorded in audit)");
                        if (!reason) return;
                        await post("enable", { approval_reason: reason });
                      }}
                      data-testid={`${testid}-enable-btn`}>
                Enable
              </Button>
              <Button size="sm" variant="outline" disabled={!isLive}
                      onClick={() => post("disable", {})}
                      data-testid={`${testid}-disable-btn`}>
                Kill switch
              </Button>
            </div>
            {isLive && (
              <div className="text-[11px] text-emerald-700 mt-1">
                Enabled by {(st.live_enabled_by || "").slice(-6)} at {(st.live_enabled_at || "").slice(0, 16)}
              </div>
            )}
          </div>
        </li>
      </ol>
    </Card>
  );
}
